import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CrmClient } from 'src/app/crm/crm.model';
import { CrmPartyContextService } from 'src/app/shared/crm-client-contact-picker/crm-party-context.service';
import { Driver } from 'src/app/fleet/driver/driver.model';
import { DriverService } from 'src/app/fleet/driver/driver.service';
import { VehicleMin } from 'src/app/fleet/vehicle/vehicle-min.model';
import { VehicleService } from 'src/app/fleet/vehicle/vehicle.service';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerFacility } from '../models/container-facility.model';
import {
  ContainerYardAppointment,
  ContainerYardChargeRule,
  ContainerYardEir,
  ContainerYardOccupancyOverview,
  ContainerYardVisit
} from '../models/container-terminal-yard.model';
import { ProductsService } from 'src/app/order/product.service';
import { Products } from 'src/app/order/models/product.model';
import { ProductVariant } from 'src/app/order/models/product-variant.model';

type YardOpsMode = 'occupancy' | 'appointments' | 'eirs' | 'charges';

interface AppointmentOption {
  label: string;
  value: string | number;
  hint?: string;
}

@Component({
  selector: 'app-yard-operations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './yard-operations.component.html',
  styleUrl: './yard-operations.component.css'
})
export class YardOperationsComponent implements OnInit {
  mode: YardOpsMode = 'occupancy';
  isLoading = false;
  isSubmitting = false;
  appointmentDialog = false;
  chargeRuleDialog = false;
  editingAppointment: ContainerYardAppointment | null = null;

  facilities: ContainerFacility[] = [];
  visits: ContainerYardVisit[] = [];
  appointments: ContainerYardAppointment[] = [];
  chargeRules: ContainerYardChargeRule[] = [];
  logisticsProducts: Products[] = [];
  vehicles: VehicleMin[] = [];
  drivers: Driver[] = [];
  transporterClients: CrmClient[] = [];
  containerOptions: AppointmentOption[] = [];
  truckPlateOptions: AppointmentOption[] = [];
  driverOptions: AppointmentOption[] = [];
  carrierOptions: AppointmentOption[] = [];
  occupancy: ContainerYardOccupancyOverview = {};
  eirsByVisit = new Map<number, ContainerYardEir[]>();

  appointmentForm!: FormGroup;
  chargeRuleForm!: FormGroup;

  appointmentStatuses = [
    { label: 'Scheduled', value: 'SCHEDULED' },
    { label: 'Checked In', value: 'CHECKED_IN' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'No Show', value: 'NO_SHOW' }
  ];
  appointmentTypes = [
    { label: 'Gate In', value: 'GATE_IN' },
    { label: 'Gate Out', value: 'GATE_OUT' },
    { label: 'Dock Move', value: 'DOCK' },
    { label: 'Inspection', value: 'INSPECTION' }
  ];
  chargeTypes = [
    'STORAGE', 'DEMURRAGE', 'DETENTION', 'LIFT_ON', 'LIFT_OFF', 'GATE_IN', 'GATE_OUT',
    'REEFER_PLUGIN', 'INSPECTION', 'WEIGHING', 'CLEANING', 'REPAIR'
  ].map(value => ({ label: this.label(value), value }));
  chargeBasisOptions = [
    { label: 'Per Day', value: 'PER_DAY' },
    { label: 'Flat', value: 'FLAT' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private sessionStorageService: SessionStorageService,
    private facilityService: ContainerFacilityService,
    private yardService: ContainerTerminalYardService,
    private vehicleService: VehicleService,
    private driverService: DriverService,
    private crmPartyContext: CrmPartyContextService,
    private productsService: ProductsService
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.route.data.subscribe(data => {
      this.mode = (data['mode'] || 'occupancy') as YardOpsMode;
      this.load();
    });
  }

  get title(): string {
    switch (this.mode) {
      case 'appointments': return 'Truck Appointments';
      case 'eirs': return 'EIR & Condition';
      case 'charges': return 'Yard Charges';
      default: return 'Yard Occupancy';
    }
  }

  get subtitle(): string {
    switch (this.mode) {
      case 'appointments': return 'Schedule, check in, complete, cancel, and link gate appointments.';
      case 'eirs': return 'Gate-in/out EIR receipts with seal, condition, and damage capture.';
      case 'charges': return 'Configure yard charge rules and recalculate estimated visit charges.';
      default: return 'Facility and area slot utilization with active visits, holds, and expired free-time.';
    }
  }

  get metrics(): PageMetric[] {
    if (this.mode === 'appointments') {
      return [
        { label: 'Appointments', value: this.appointments.length, tone: 'info' },
        { label: 'Scheduled', value: this.appointments.filter(item => item.status === 'SCHEDULED').length, tone: 'warn' },
        { label: 'Checked In', value: this.appointments.filter(item => item.status === 'CHECKED_IN').length, tone: 'info' },
        { label: 'Completed', value: this.appointments.filter(item => item.status === 'COMPLETED').length, tone: 'success' }
      ];
    }
    if (this.mode === 'charges') {
      return [
        { label: 'Charge Rules', value: this.chargeRules.length, tone: 'warn' },
        { label: 'Active Rules', value: this.chargeRules.filter(rule => rule.active !== false).length, tone: 'success' },
        { label: 'Active Visits', value: this.visits.length, tone: 'info' },
        { label: 'Estimated Charges', value: this.money(this.visits.reduce((sum, visit) => sum + Number(visit.estimatedChargeAmount || 0), 0)), tone: 'warn' }
      ];
    }
    if (this.mode === 'eirs') {
      return [
        { label: 'EIRs', value: this.allEirs().length, tone: 'info' },
        { label: 'Gate In', value: this.allEirs().filter(eir => eir.eirType === 'GATE_IN').length, tone: 'success' },
        { label: 'Gate Out', value: this.allEirs().filter(eir => eir.eirType === 'GATE_OUT').length, tone: 'warn' },
        { label: 'Damaged', value: this.allEirs().filter(eir => eir.damageObserved).length, tone: 'danger' }
      ];
    }
    return [
      { label: 'Total Slots', value: this.occupancy.totalSlots || 0, tone: 'info' },
      { label: 'Occupied Slots', value: this.occupancy.occupiedSlots || 0, tone: 'warn' },
      { label: 'Available Slots', value: this.occupancy.availableSlots || 0, tone: 'success' },
      { label: 'Active Visits', value: this.occupancy.activeVisits || 0, tone: 'info' },
      { label: 'Expired Free Time', value: this.occupancy.expiredFreeTimeCount || 0, tone: 'danger' }
    ];
  }

  load(): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }
    this.isLoading = true;
    this.facilityService.findAll(new HttpParams().set('owningCompanyId', String(companyId)).set('size', '200')).subscribe({
      next: page => this.facilities = page.content || [],
      error: () => this.facilities = []
    });
    this.yardService.findInventory(new HttpParams().set('companyId', String(companyId)).set('status', 'ACTIVE')).subscribe({
      next: visits => {
        this.visits = visits || [];
        this.rebuildAppointmentOptions();
        this.loadEirs();
        this.isLoading = false;
      },
      error: () => {
        this.visits = [];
        this.isLoading = false;
      }
    });
    this.yardService.occupancy(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: overview => this.occupancy = overview || {},
      error: () => this.occupancy = {}
    });
    this.yardService.findAppointments(companyId).subscribe({
      next: appointments => {
        this.appointments = appointments || [];
        this.rebuildAppointmentOptions();
      },
      error: () => {
        this.appointments = [];
        this.rebuildAppointmentOptions();
      }
    });
    this.yardService.findChargeRules(companyId).subscribe({
      next: rules => this.chargeRules = rules || [],
      error: () => this.chargeRules = []
    });
    this.productsService.getLogisticsCatalog(companyId, 'CONTAINER_YARD').subscribe({
      next: products => this.logisticsProducts = products || [],
      error: () => this.logisticsProducts = []
    });
    this.loadAppointmentResourceOptions(companyId);
  }

  openAppointment(appointment?: ContainerYardAppointment): void {
    this.editingAppointment = appointment || null;
    this.appointmentForm.reset({
      id: appointment?.id || null,
      facilityId: appointment?.facilityId || null,
      containerCaseId: appointment?.containerCaseId || null,
      visitId: appointment?.visitId || null,
      containerNumber: appointment?.containerNumber || '',
      appointmentType: appointment?.appointmentType || 'GATE_IN',
      scheduledStartAt: appointment?.scheduledStartAt ? new Date(appointment.scheduledStartAt) : new Date(),
      scheduledEndAt: appointment?.scheduledEndAt ? new Date(appointment.scheduledEndAt) : null,
      status: appointment?.status || 'SCHEDULED',
      truckPlateNumber: appointment?.truckPlateNumber || '',
      vehicleId: appointment?.vehicleId || null,
      driverName: appointment?.driverName || '',
      driverId: appointment?.driverId || null,
      carrierName: appointment?.carrierName || '',
      carrierCrmClientId: appointment?.carrierCrmClientId || null,
      carrierCrmClientName: appointment?.carrierCrmClientName || '',
      carrierCrmClientNumber: appointment?.carrierCrmClientNumber || '',
      referenceNumber: appointment?.referenceNumber || ''
    });
    this.appointmentDialog = true;
  }

  saveAppointment(): void {
    const companyId = this.companyId();
    if (!companyId || this.appointmentForm.invalid) {
      this.appointmentForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.yardService.saveAppointment(companyId, this.appointmentPayload()).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.appointmentDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Appointment saved', detail: 'Truck appointment was saved.' });
        this.load();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Appointment failed', detail: error?.error?.message || 'Could not save appointment.' });
      }
    });
  }

  openChargeRule(rule?: ContainerYardChargeRule): void {
    this.chargeRuleForm.reset({
      id: rule?.id || null,
      facilityId: rule?.facilityId || null,
      areaId: rule?.areaId || null,
      containerType: rule?.containerType || '',
      productId: rule?.productId || null,
      variantId: rule?.variantId || null,
      chargeType: rule?.chargeType || 'STORAGE',
      chargeBasis: rule?.chargeBasis || 'PER_DAY',
      freeDays: rule?.freeDays || 0,
      currencyCode: rule?.currencyCode || 'SAR',
      rateAmount: rule?.rateAmount || 0,
      active: rule?.active !== false
    }, { emitEvent: false });
    this.chargeRuleDialog = true;
  }

  saveChargeRule(): void {
    const companyId = this.companyId();
    if (!companyId || this.chargeRuleForm.invalid) {
      this.chargeRuleForm.markAllAsTouched();
      return;
    }
    const value = this.chargeRuleForm.getRawValue();
    const request = value.id
      ? this.yardService.updateChargeRule(companyId, value.id, value)
      : this.yardService.createChargeRule(companyId, value);
    this.isSubmitting = true;
    request.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.chargeRuleDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Charge rule saved', detail: 'Yard charge rule was saved.' });
        this.load();
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Charge rule failed', detail: error?.error?.message || 'Could not save charge rule.' });
      }
    });
  }

  recalculate(visit: ContainerYardVisit): void {
    const companyId = this.companyId();
    if (!companyId || !visit.id) {
      return;
    }
    this.yardService.recalculateCharges(companyId, visit.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Charges recalculated', detail: 'Estimated yard charges were refreshed.' });
        this.load();
      }
    });
  }

  openInventory(row?: { areaId?: number; facilityId?: number }): void {
    const queryParams: Record<string, number> = {};
    if (row?.facilityId) queryParams['facilityId'] = row.facilityId;
    if (row?.areaId) queryParams['areaId'] = row.areaId;
    this.router.navigate(['/container-tracking/inventory'], { queryParams });
  }

  openCase(visit: ContainerYardVisit): void {
    if (visit.containerCaseId) {
      this.router.navigate(['/container-tracking/cases/view', visit.containerCaseId], { queryParams: { tab: 'storage-legs' } });
    }
  }

  openEir(eir: ContainerYardEir): void {
    const companyId = this.companyId();
    if (companyId && eir.id) {
      window.open(this.yardService.eirPdfUrl(companyId, eir.id), '_blank');
    }
  }

  allEirs(): ContainerYardEir[] {
    return Array.from(this.eirsByVisit.values()).flat();
  }

  eirsForVisit(visitId?: number): ContainerYardEir[] {
    return visitId ? (this.eirsByVisit.get(visitId) || []) : [];
  }

  facilityName(id?: number): string {
    return this.facilities.find(facility => facility.id === id)?.facilityName || (id ? `Facility #${id}` : '-');
  }

  severity(value?: string | boolean): string {
    if (value === true) return 'success';
    if (value === false) return 'secondary';
    switch ((value || '').toString().toUpperCase()) {
      case 'COMPLETED':
      case 'GOOD':
      case 'ACTIVE':
      case 'SCHEDULED': return 'success';
      case 'CHECKED_IN':
      case 'PER_DAY':
      case 'GATE_IN': return 'info';
      case 'DAMAGED':
      case 'CANCELLED':
      case 'NO_SHOW':
      case 'ON_HOLD': return 'danger';
      default: return 'warn';
    }
  }

  label(value?: string | null): string {
    return (value || '').toLowerCase().split('_').filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  selectedChargeProduct(): Products | undefined {
    return this.logisticsProducts.find(product => product.productID === this.chargeRuleForm?.get('productId')?.value);
  }

  chargeProductVariants(): ProductVariant[] {
    return this.selectedChargeProduct()?.productVariants || [];
  }

  productName(id?: number | null): string {
    return this.logisticsProducts.find(product => product.productID === id)?.name || 'Manual charge';
  }

  appointmentFacilityName(): string {
    return this.facilityName(this.appointmentForm?.get('facilityId')?.value);
  }

  appointmentTypeName(): string {
    const value = this.appointmentForm?.get('appointmentType')?.value;
    return this.appointmentTypes.find(option => option.value === value)?.label || this.label(value) || 'Gate appointment';
  }

  appointmentWindowLabel(): string {
    const start = this.appointmentForm?.get('scheduledStartAt')?.value;
    if (!start) {
      return 'Window pending';
    }
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(start));
  }

  onAppointmentVehicleChange(value: string | number | null): void {
    const vehicle = this.selectedVehicle(value);
    this.appointmentForm.patchValue({
      truckPlateNumber: vehicle ? this.vehicleLabel(vehicle) : (typeof value === 'string' ? value : '')
    }, { emitEvent: false });
  }

  onAppointmentDriverChange(value: string | number | null): void {
    const driver = this.selectedDriver(value);
    this.appointmentForm.patchValue({
      driverName: driver ? this.driverLabel(driver) : (typeof value === 'string' ? value : '')
    }, { emitEvent: false });
  }

  onAppointmentCarrierChange(value: string | number | null): void {
    const carrier = this.selectedCarrier(value);
    const carrierName = carrier ? this.crmPartyContext.clientName(carrier) : (typeof value === 'string' ? value : '');
    this.appointmentForm.patchValue({
      carrierName,
      carrierCrmClientName: carrier ? carrierName : '',
      carrierCrmClientNumber: carrier?.clientNumber || ''
    }, { emitEvent: false });
  }

  private loadAppointmentResourceOptions(companyId: number): void {
    const company = this.sessionStorageService.getCompany();
    const transportationAgencyId = company?.transportationAgencyId || this.sessionStorageService.getTransportationAgency()?.rowId;
    const vehicleRequest = transportationAgencyId
      ? this.vehicleService.findVehicleMinsByTransportationAgencyId(transportationAgencyId)
      : this.vehicleService.getFreelanceVehicleMinsByCompanyId(companyId);
    vehicleRequest.subscribe({
      next: response => {
        this.vehicles = response.items || [];
        this.rebuildAppointmentOptions();
      },
      error: () => {
        this.vehicles = [];
        this.rebuildAppointmentOptions();
      }
    });
    const driverRequest = transportationAgencyId
      ? this.driverService.findDriversByTransportationAgencyId(0, 250, transportationAgencyId)
      : this.driverService.getFreelanceDriversByCompanyId(0, 250, companyId);
    driverRequest.subscribe({
      next: response => {
        this.drivers = response.items || [];
        this.rebuildAppointmentOptions();
      },
      error: () => {
        this.drivers = [];
        this.rebuildAppointmentOptions();
      }
    });
    this.crmPartyContext.loadClients(companyId, ['TRANSPORTER']).subscribe({
      next: clients => {
        this.transporterClients = clients || [];
        this.rebuildAppointmentOptions();
      },
      error: () => {
        this.transporterClients = [];
        this.rebuildAppointmentOptions();
      }
    });
  }

  private rebuildAppointmentOptions(): void {
    this.containerOptions = this.uniqueOptions([
      ...this.visits.map(visit => ({
        label: visit.containerNumber || '',
        value: visit.containerNumber || '',
        hint: [visit.billOfLadingNumber, visit.bookingNumber].filter(Boolean).join(' | ')
      })),
      ...this.appointments.map(appointment => ({
        label: appointment.containerNumber || '',
        value: appointment.containerNumber || '',
        hint: appointment.referenceNumber || ''
      }))
    ]);
    this.truckPlateOptions = this.uniqueOptions([
      ...this.vehicles.map(vehicle => ({
        label: this.vehicleLabel(vehicle),
        value: vehicle.vehicleId,
        hint: vehicle.registrationNumber || vehicle.rentalVendorName || ''
      })),
      ...this.appointments.map(appointment => ({
        label: appointment.truckPlateNumber || '',
        value: appointment.vehicleId || appointment.truckPlateNumber || '',
        hint: appointment.driverName || ''
      }))
    ]);
    this.driverOptions = this.uniqueOptions([
      ...this.drivers.map(driver => ({
        label: this.driverLabel(driver),
        value: this.driverOptionValue(driver),
        hint: driver.phoneNumber || driver.currentVehicle?.plateNumber || ''
      })),
      ...this.appointments.map(appointment => ({
        label: appointment.driverName || '',
        value: appointment.driverId || appointment.driverName || '',
        hint: appointment.truckPlateNumber || ''
      }))
    ]);
    this.carrierOptions = this.uniqueOptions([
      ...this.transporterClients.map(client => ({
        label: this.crmPartyContext.clientName(client),
        value: client.id || this.crmPartyContext.clientName(client),
        hint: client.clientNumber || client.linkedTransportationAgencyName || 'CRM transporter'
      })),
      ...this.appointments.map(appointment => ({
        label: appointment.carrierCrmClientName || appointment.carrierName || '',
        value: appointment.carrierCrmClientId || appointment.carrierName || '',
        hint: 'Appointment history'
      }))
    ]);
  }

  private uniqueOptions(options: AppointmentOption[]): AppointmentOption[] {
    const map = new Map<string, AppointmentOption>();
    options
      .filter(option => !!option.value)
      .forEach(option => {
        const key = String(option.value).trim().toLowerCase();
        if (key && !map.has(key)) {
          map.set(key, option);
        }
      });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  private appointmentPayload(): ContainerYardAppointment {
    const payload = { ...this.appointmentForm.getRawValue() } as ContainerYardAppointment;
    const vehicle = this.selectedVehicle(payload.vehicleId || null);
    const driver = this.selectedDriver(payload.driverId || null);
    const carrier = this.selectedCarrier(payload.carrierCrmClientId || null);
    if (vehicle) {
      payload.vehicleId = vehicle.vehicleId;
      payload.truckPlateNumber = this.vehicleLabel(vehicle);
    } else {
      payload.vehicleId = undefined;
    }
    if (driver) {
      payload.driverId = driver.id;
      payload.driverName = this.driverLabel(driver);
    } else {
      payload.driverId = undefined;
    }
    if (carrier) {
      payload.carrierCrmClientId = carrier.id;
      payload.carrierName = this.crmPartyContext.clientName(carrier);
      payload.carrierCrmClientName = payload.carrierName;
      payload.carrierCrmClientNumber = carrier.clientNumber || '';
    } else {
      payload.carrierCrmClientId = undefined;
    }
    return payload;
  }

  private vehicleLabel(vehicle?: VehicleMin | null): string {
    return vehicle?.plateNumber || vehicle?.registrationNumber || '';
  }

  private driverLabel(driver?: Driver | null): string {
    return driver?.legalName || driver?.name || driver?.phoneNumber || '';
  }

  private driverOptionValue(driver: Driver): string | number {
    return driver.id || this.driverLabel(driver);
  }

  private selectedVehicle(value: string | number | null | undefined): VehicleMin | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    return this.vehicles.find(vehicle => String(vehicle.vehicleId) === String(value));
  }

  private selectedDriver(value: string | number | null | undefined): Driver | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    return this.drivers.find(driver => String(this.driverOptionValue(driver)) === String(value));
  }

  private selectedCarrier(value: string | number | null | undefined): CrmClient | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    return this.transporterClients.find(client => String(client.id) === String(value));
  }

  money(value?: number): string {
    return `SAR ${Number(value || 0).toFixed(2)}`;
  }

  private initForms(): void {
    this.appointmentForm = this.fb.group({
      id: [null],
      facilityId: [null, Validators.required],
      containerCaseId: [null],
      visitId: [null],
      containerNumber: [''],
      appointmentType: ['GATE_IN', Validators.required],
      scheduledStartAt: [new Date(), Validators.required],
      scheduledEndAt: [null],
      status: ['SCHEDULED', Validators.required],
      truckPlateNumber: [''],
      vehicleId: [null],
      driverName: [''],
      driverId: [null],
      carrierName: [''],
      carrierCrmClientId: [null],
      carrierCrmClientName: [''],
      carrierCrmClientNumber: [''],
      referenceNumber: ['']
    });
    this.chargeRuleForm = this.fb.group({
      id: [null],
      facilityId: [null, Validators.required],
      areaId: [null],
      containerType: [''],
      productId: [null],
      variantId: [null],
      chargeType: ['STORAGE', Validators.required],
      chargeBasis: ['PER_DAY', Validators.required],
      freeDays: [0],
      currencyCode: ['SAR', Validators.required],
      rateAmount: [0, Validators.required],
      active: [true]
    });
    this.chargeRuleForm.get('productId')?.valueChanges.subscribe(productId => {
      if (!productId) {
        this.chargeRuleForm.patchValue({ variantId: null }, { emitEvent: false });
        return;
      }
      const product = this.logisticsProducts.find(item => item.productID === productId);
      if (!product) return;
      const usage = product.logisticsUsages?.find(item => item.usageModule === 'CONTAINER_YARD' && item.active !== false);
      const variants = product.productVariants || [];
      const variantId = usage?.defaultVariantId || (variants.length === 1 ? variants[0].variantID : null);
      const variant = variants.find(item => item.variantID === variantId);
      this.chargeRuleForm.patchValue({
        variantId,
        chargeType: usage?.operationalCode || this.chargeRuleForm.get('chargeType')?.value,
        currencyCode: product.currencyCode || this.chargeRuleForm.get('currencyCode')?.value,
        rateAmount: variant?.sellingRateAdjustment ?? product.sellingRate ?? product.standardRate ?? 0
      }, { emitEvent: false });
    });
  }

  private loadEirs(): void {
    this.eirsByVisit.clear();
    this.visits.filter(visit => !!visit.id).forEach(visit => {
      this.yardService.findEirs(visit.id!).subscribe({
        next: eirs => this.eirsByVisit.set(visit.id!, eirs || []),
        error: () => this.eirsByVisit.set(visit.id!, [])
      });
    });
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id || 0;
  }
}
